import React from "react";
import { DateTime } from "luxon";
import {
  Constraint,
  OptimizedGantt,
  Identifiable,
  Project,
} from "@planzone/dto";
import { Context } from "@scenes/optimizedGantt/context";
import { Views } from "@scenes/optimizedGantt/api/views";
import { useElementDrag } from "../utils/drag";
import { useElementEdition } from "../utils/edit";
import { useElementContext } from "../context";
import { getPreciseDate } from "../utils/getDate";
import { getSnapQuantum } from "../root/utils";

type ResizeOptions = {
  element: OptimizedGantt[number];
  graphRef: React.RefObject<HTMLDivElement | null>;
  project: Identifiable<Project> | null;
  dates: DateTime[];
};

function useResize(
  { element, graphRef, project, dates }: ResizeOptions,
  editElement: (offset: number) => OptimizedGantt[number],
  side: "left" | "right",
) {
  const { state, reducer } = useElementContext();
  const { editElement: edit } = useElementEdition(element);
  const { startDrag } = useElementDrag(graphRef, project, {
    edit: (offset) => reducer.resize((state) => ({ ...state, offset }), side),
    stopDrag: () => {
      edit(editElement(state.resize[side].offset));
      reducer.resize((state) => ({ ...state, isResizing: false }), side);
    },
  });

  return {
    isResizing: state.resize[side].isResizing,
    offset: state.resize[side].offset,
    startResize: (initialPos: number) => {
      reducer.resize((state) => ({ ...state, isResizing: true }), side);
      startDrag(initialPos);
    },
  };
}

function useLeftResize(options: ResizeOptions) {
  const { cell, currentView } = React.useContext(Context);
  const isDaily = currentView === Views.DAILY;

  return useResize(
    options,
    (offset) => {
      const { dates, element } = options;

      if (isDaily) {
        const hourIndex = Math.round(offset / getSnapQuantum(cell, isDaily));

        return {
          ...element,
          startDate: dates[0].plus({ hours: hourIndex }).toUTC().toISO() ?? "",
          endDate: element.endDate,
        };
      }

      const startDateIndex = Math.round(offset / cell.width);

      return {
        ...element,
        startDate:
          dates[0].plus({ days: startDateIndex }).toUTC().toISO() ?? "",
        endDate: element.endDate,
      };
    },
    "left",
  );
}

function useRightResize(options: ResizeOptions, leftMargin: number) {
  const { cell, currentView } = React.useContext(Context);
  const isDaily = currentView === Views.DAILY;
  const userTimezone = DateTime.local().zoneName;

  return useResize(
    options,
    (offset) => {
      const { dates, element } = options;

      if (isDaily) {
        const hourIndex = Math.round(offset / getSnapQuantum(cell, isDaily));
        const edge = dates[0].plus({ hours: hourIndex });
        // A date-only element (midnight start) keeps the day views' write
        // convention when its right edge lands on a midnight: store the
        // midnight of the last INCLUDED day, which the display re-extends
        // through that day. Storing the edge itself would grow the element
        // by one day on every such resize.
        const isEdgeMidnight =
          edge.toMillis() === edge.startOf("day").toMillis();
        const start = getPreciseDate(element.startDate, userTimezone);
        const isDateOnlyStart =
          !element.startDate ||
          start.toMillis() === start.startOf("day").toMillis();

        return {
          ...element,
          startDate: element.startDate,
          endDate:
            (isEdgeMidnight && isDateOnlyStart ? edge.minus({ days: 1 }) : edge)
              .toUTC()
              .toISO() ?? "",
        };
      }

      const endDateIndex = Math.round(offset / cell.width) - 1;

      return {
        ...element,
        startDate: element.startDate,
        endDate: dates[0].plus({ days: endDateIndex }).toUTC().toISO() ?? "",
      };
    },
    "right",
  );
}

function isStartConstrained(
  element: OptimizedGantt[number],
  constraints: (Identifiable<Constraint> & {
    targetStartDate: string;
    targetEndDate: string;
    targetIsDone: boolean;
    originStartDate: string;
    originEndDate: string;
    originIsDone: boolean;
  })[],
) {
  return constraints.find(
    ({ targetEntityUuid, targetDateType }) =>
      targetEntityUuid === element.uuid && targetDateType === "START",
  );
}

function isEndConstrained(
  element: OptimizedGantt[number],
  constraints: (Identifiable<Constraint> & {
    targetStartDate: string;
    targetEndDate: string;
    targetIsDone: boolean;
    originStartDate: string;
    originEndDate: string;
    originIsDone: boolean;
  })[],
) {
  return constraints.find(
    ({ targetEntityUuid, targetDateType }) =>
      targetEntityUuid === element.uuid && targetDateType === "END",
  );
}

export { useLeftResize, useRightResize, isStartConstrained, isEndConstrained };
